import { action, computed, observable, runInAction } from 'mobx'
import { type GetCurrentMemberFn } from '@/features/iam'
import type { ActivationLifecycle } from '@/infra/activation'
import type {
  ListPublishedLegendsFn,
  ListSearchableJobsFn,
} from '@/features/jobs'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { StartHandoffFn } from '@/features/shell'
import { type Observable, of, switchMap } from 'rxjs'
import type { EnsureTermsAcceptanceFn } from '@/features/account'
import { whileActive, withAbortSignal } from '@/utils/rx-util'
import { map, tap } from 'rxjs/operators'
import { fromStream, toStream } from '@/utils/mobx-util'
import { ServiceSelectionViewModel } from '@/features/dashboard/DashboardView/ServiceSelectionViewModel'
import type { CreateNewDraftJobFn } from '@/features/jobs/internal'
import type { JobListViewModelFactory } from '@/features/jobs/list/JobListViewModel'
import { memoizeAsync } from '@/utils/memoize-decorator'
import type { SearchableJob } from '@/features/jobs/list/SearchableJob'
import { CallCenterAssistanceViewModel } from '@/features/dashboard/components/CallAssistanceDialogs/CallCenterAssistanceViewModel'
import { SessionOptions } from '@/features/iam/auth/SessionOptions'

/**
 * View model for the dashboard screen.
 */
export class DashboardViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _getSessionOptions

  /**
   * Whether the DIY feature is enabled.
   */
  @observable accessor isDiyEnabled = false

  /**
   * Whether the user is a returning customer from prior years.
   */
  @observable accessor isReturningCustomerFromPriorYear = false

  /**
   * The service selection view model.
   */
  readonly serviceSelectionVm

  /**
   * The job list view model.
   */
  readonly jobListVm

  /**
   * The call center list view model.
   */
  readonly callCenterAssistanceVm

  /**
   * @see isBlockingInteractionsDueToLackOfTermsAcceptance
   */
  private readonly _isBlockingInteractionsDueToLackOfTermsAcceptance =
    fromStream(
      false, // Default value
      toStream(
        () =>
          this._getCurrentMember().workspace.config.termsOfService.showOnLogin,
        true,
      ).pipe(
        switchMap((shouldShowTerms) =>
          !shouldShowTerms
            ? of(false)
            : withAbortSignal(async (signal) => {
                const member = this._getCurrentMember()
                await this._startHandoff(member.workspace, signal)
                return this._ensureTermsAcceptance(member.workspaceId, signal)
              }).pipe(
                // Block interactions if the user declines
                map((outcome) => outcome === 'Declined'),
              ),
        ),
        // Keep stream alive while VM is active.
        whileActive(this._lifecycle.active$),
      ),
    )

  constructor(
    private readonly _getCurrentMember: GetCurrentMemberFn,
    listPublishedLegends: ListPublishedLegendsFn,
    createNewDraftJob: CreateNewDraftJobFn,
    createJobListViewModel: JobListViewModelFactory,
    newSearchableJob$: Observable<SearchableJob>,
    private readonly _listSearchableJobs: ListSearchableJobsFn,
    private readonly _ensureTermsAcceptance: EnsureTermsAcceptanceFn,
    private readonly _startHandoff: StartHandoffFn,
    sessionOptions$: Observable<SessionOptions>,
  ) {
    this._getSessionOptions = fromStream(
      SessionOptions.default,
      sessionOptions$,
    )
    this.serviceSelectionVm = new ServiceSelectionViewModel(
      () => !this.isCompletelyEmpty,
      _getCurrentMember,
      sessionOptions$,
      listPublishedLegends,
      createNewDraftJob,
    )
    this.jobListVm = createJobListViewModel({
      limit: 20,
      models: (jobs) => jobs,
    })
    this.callCenterAssistanceVm = new CallCenterAssistanceViewModel({
      getCallCenterConfig: () =>
        _getCurrentMember().workspace.config.callCenter,
      showSelfOnboarding: true,
      onSelfOnboardingAction: this.serviceSelectionVm.startNewService,
    })
    this._lifecycle.manage(this.serviceSelectionVm)
    this._lifecycle.manage(this.jobListVm)
    this._lifecycle.manage(this.callCenterAssistanceVm)

    this._lifecycle.manage(
      newSearchableJob$.pipe(
        // Add the job to the list.
        tap((job) => this.jobListVm.scroller.prepend(job)),
      ),
    )
  }

  /**
   * Weather the call center is enabled.
   */
  @computed
  get callCenterEnabled() {
    return this.callCenterAssistanceVm.enabled
  }

  /**
   * Session return options
   */
  @computed
  get sessionReturnOptions() {
    return this._getSessionOptions().returnOptions ?? null
  }

  /**
   * Whether to block interactions due to lack of terms acceptance.
   * This depends on the workspace config for whether to check
   * terms.
   */
  @computed
  get isBlockingInteractionsDueToLackOfTermsAcceptance() {
    return this._isBlockingInteractionsDueToLackOfTermsAcceptance()
  }

  /**
   *  The current user.
   */
  @computed
  get member() {
    return this._getCurrentMember()
  }

  /**
   * Whether the dashboard is completely empty.
   */
  @computed
  get isCompletelyEmpty() {
    return (
      this.jobListVm.scroller.items.length === 0 &&
      this.jobListVm.scroller.isDone
    )
  }

  /**
   * Whether we're showing the loading state.
   */
  @computed
  get isShowingLoadingState() {
    return this.jobListVm.isFetchingInitial
  }

  /**
   * Whether we're ready to start syncing state.
   */
  @computed get isReady() {
    return (
      !this.isShowingLoadingState &&
      this.serviceSelectionVm.listPublishedLegends.resolved
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()

    void this.checkIsReturningCustomerFromPriorYear(this._lifecycle.signal)
    void this.fetchJobs(this._lifecycle.signal)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Shows the service types dialog or the call center dialog, if enabled.
   */
  @action.bound
  requestService() {
    if (this.callCenterEnabled) {
      this.callCenterAssistanceVm.callOptionsModalVm.show()
      return
    }

    this.serviceSelectionVm.startNewService()
  }

  /**
   * Lists the searchable jobs.
   */
  fetchJobs(signal?: AbortSignal) {
    const startOfYear = getStartOfYearString()
    return this.jobListVm.fetch({
      signal,
      staleWhileRevalidate: true,
      orderBy: 'date_last_activity desc',
      filter: `(status = "CLOSED" AND date_completed >= "${startOfYear}") OR status != "CLOSED"`,
    })
  }

  /**
   * Checks if the user is a returning customer from a prior year by querying
   * for jobs that have been closed prior to the current year.
   */
  @memoizeAsync()
  async checkIsReturningCustomerFromPriorYear(signal?: AbortSignal) {
    const startOfYear = getStartOfYearString()
    const result = await this._listSearchableJobs(
      {
        limit: 1,
        filter: `status = "CLOSED" AND date_completed < "${startOfYear}"`,
        after: null,
      },
      signal,
    )
    signal?.throwIfAborted()

    runInAction(() => {
      this.isReturningCustomerFromPriorYear = Boolean(
        result.total && result.total > 0,
      )
    })
  }
}

function getStartOfYearString() {
  const now = new Date()
  return `${now.getFullYear()}-01-01`
}
